/**
 * Phase 27.45 — Event Request detail dialog.
 *
 * Shows the full request + approval workflow actions available to the
 * current viewer. Funding split lines are hidden by the backend for
 * agents; we simply render whatever the API returned.
 *
 * Kept as a modal instead of a route so the tab stays the source of
 * truth for filtering and pagination.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, CheckCircle2, XCircle, HelpCircle, DollarSign, MapPin, User,
  Calendar, Users, Flag, ClipboardList, ExternalLink, Paperclip, Upload,
  Trash2, Download, Plus, CreditCard, Pencil, Ban, RotateCcw, Wallet,
  TrendingUp, ArrowRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { eventRequestsAPI, deletionAuditAPI } from '@/lib/api';
import DeleteRequestDialog from '@/components/marketing/DeleteRequestDialog';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, isAgencyContext } from '@/lib/utils';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import EventRequestFormDialog from './EventRequestFormDialog';
import LiveCaptureButton from '@/components/agent-events/LiveCaptureButton';
import ManageParticipantsDialog from './ManageParticipantsDialog';

const STATUS_META = {
  submitted:           { label: 'Submitted',       tone: 'bg-slate-100 text-slate-700 border-slate-200' },
  agency_review:       { label: 'Agency Review',   tone: 'bg-blue-100 text-blue-800 border-blue-200' },
  kr_review:           { label: 'KR Review',       tone: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  needs_information:   { label: 'Needs Info',      tone: 'bg-amber-100 text-amber-800 border-amber-300' },
  approved:            { label: 'Approved',        tone: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  partially_approved:  { label: 'Partially Appr.', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  declined:            { label: 'Declined',        tone: 'bg-rose-100 text-rose-800 border-rose-300' },
  cancelled:           { label: 'Cancelled',       tone: 'bg-slate-100 text-slate-500 border-slate-300 line-through' },
  completed:           { label: 'Completed',       tone: 'bg-slate-200 text-slate-700 border-slate-300' },
};

function fmtMoney(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

export default function EventRequestDetailDialog({ requestId, isKR, onOpenChange, onChanged }) {
  const open = !!requestId;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [action, setAction] = useState(null); // 'approve' | 'decline' | 'needs_info' | 'adjust' | 'cancel' | 'reopen'
  const [actionForm, setActionForm] = useState({
    amount_approved: '', kr_amount: '', agency_amount: '',
    internal_notes: '', reason: '', note: '',
  });
  const [saving, setSaving] = useState(false);
  // Phase 27.48 — flexibility dialogs
  const [showEdit, setShowEdit] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    try {
      const r = await eventRequestsAPI.get(requestId);
      setData(r.data);
      setActionForm((p) => ({
        ...p, amount_approved: r.data?.amount_requested ?? '',
      }));
      // Phase 27.53 — load ROI metrics in parallel (best-effort).
      eventRequestsAPI.leadMetrics(requestId)
        .then((mr) => setMetrics(mr.data))
        .catch(() => setMetrics(null));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load request');
      onOpenChange?.(false);
    } finally {
      setLoading(false);
    }
  }, [requestId, onOpenChange]);

  useEffect(() => {
    if (open) {
      setAction(null);
      load();
    } else {
      setData(null);
    }
  }, [open, load]);

  const runAction = async (fn, successMsg) => {
    setSaving(true);
    try {
      await fn();
      toast.success(successMsg);
      setAction(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally {
      setSaving(false);
    }
  };

  const submitAgencyReview = () => runAction(
    () => eventRequestsAPI.submitAgencyReview(requestId, actionForm.note || null),
    'Sent to Agency Review',
  );
  const submitKrReview = () => runAction(
    () => eventRequestsAPI.submitKrReview(requestId, actionForm.note || null),
    'Sent to KR Review',
  );
  const confirmApprove = () => runAction(
    () => eventRequestsAPI.krApprove(requestId, {
      amount_approved: Number(actionForm.amount_approved || 0),
      kr_amount: actionForm.kr_amount !== '' ? Number(actionForm.kr_amount) : null,
      agency_amount: actionForm.agency_amount !== '' ? Number(actionForm.agency_amount) : null,
      internal_notes: actionForm.internal_notes || null,
    }),
    'Request approved',
  );
  const confirmDecline = () => runAction(
    () => eventRequestsAPI.decline(requestId, actionForm.reason),
    'Request declined',
  );
  const confirmNeedsInfo = () => runAction(
    () => eventRequestsAPI.needsInfo(requestId, actionForm.note),
    'Requester notified',
  );
  // Phase 27.48 — new flexibility actions
  const confirmAdjust = () => runAction(
    () => eventRequestsAPI.adjustApproval(requestId, {
      amount_approved: actionForm.amount_approved !== '' ? Number(actionForm.amount_approved) : null,
      kr_amount: actionForm.kr_amount !== '' ? Number(actionForm.kr_amount) : null,
      agency_amount: actionForm.agency_amount !== '' ? Number(actionForm.agency_amount) : null,
      internal_notes: actionForm.internal_notes || null,
      reason: actionForm.reason || null,
    }),
    'Approval adjusted',
  );
  const confirmCancel = () => runAction(
    () => eventRequestsAPI.cancel(requestId, actionForm.reason || null),
    'Event request cancelled',
  );
  const confirmReopen = () => runAction(
    () => eventRequestsAPI.reopen(requestId, actionForm.note || null),
    'Event request reopened',
  );
  const confirmHardDelete = async ({ reason, reasonPreset }) => {
    setSaving(true);
    try {
      const r = await eventRequestsAPI.delete(requestId, {
        reason, reason_preset: reasonPreset,
      });
      const auditId = r?.data?.audit_id;
      toast.success('Event request deleted', {
        duration: 10_000,
        action: auditId ? {
          label: 'Undo',
          onClick: async () => {
            try {
              await deletionAuditAPI.restore(auditId);
              toast.success('Event request restored');
              onChanged?.();
            } catch (err) {
              toast.error(err?.response?.data?.detail || 'Restore failed');
            }
          },
        } : undefined,
      });
      setConfirmDelete(false);
      onChanged?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const status = data?.status;
  const isAgencyAdmin = !!user && isAgencyContext(user);
  // KR can manage anywhere except completed; agency admin same, scoped
  // to their own agency (the backend re-validates).
  const canManage = (isKR || isAgencyAdmin) && status && status !== 'completed';
  // Phase 27.50.B.9 — Only KR can delete requests.
  const canDelete = isKR && status !== 'completed';
  const canCancel = canManage && !['cancelled', 'declined'].includes(status);
  const canReopen = (isKR || isAgencyAdmin) && ['declined', 'cancelled'].includes(status);
  const canAdjust = isKR && ['approved', 'partially_approved'].includes(status);
  const showApprove = isKR && ['submitted', 'kr_review', 'agency_review', 'needs_information'].includes(status);
  const showDecline = isKR && !['approved', 'partially_approved', 'declined', 'cancelled', 'completed'].includes(status);
  const showNeedsInfo = isKR && !['approved', 'partially_approved', 'declined', 'cancelled', 'completed'].includes(status);
  const showSendToAgency = status === 'submitted' && !!data?.requester_agency_id;
  const showSendToKr = ['submitted', 'agency_review', 'needs_information'].includes(status);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto"
        data-testid="event-request-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            {data?.event_name || 'Event Request'}
            {status && (
              <Badge
                variant="outline"
                className={`${STATUS_META[status]?.tone || 'bg-slate-100'} text-[11px] font-medium`}
              >
                {STATUS_META[status]?.label || status}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading || !data ? (
          <div className="py-12 flex items-center justify-center">
            <Loader2 className="animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="space-y-5 text-sm">
            {/* ── Event summary ─────────────────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-start gap-2">
                <Calendar size={15} className="text-slate-400 mt-0.5" />
                <div>
                  <div className="text-slate-500 text-xs">Event date</div>
                  <div className="font-medium text-slate-900">
                    {fmtDate(data.event_date)} · {data.start_time}–{data.end_time}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin size={15} className="text-slate-400 mt-0.5" />
                <div>
                  <div className="text-slate-500 text-xs">Venue</div>
                  <div className="font-medium text-slate-900">
                    {data.venue || '—'}
                    {data.address ? ` · ${data.address}` : ''}
                  </div>
                  <div className="text-xs text-slate-500">
                    {[data.city, data.state, data.zip_code].filter(Boolean).join(', ')}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <User size={15} className="text-slate-400 mt-0.5" />
                <div>
                  <div className="text-slate-500 text-xs">Requester</div>
                  <div className="font-medium text-slate-900">{data.requester_name}</div>
                  <div className="text-xs text-slate-500 capitalize">
                    {(data.requester_role || '').replace('_', ' ')}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Flag size={15} className="text-slate-400 mt-0.5" />
                <div>
                  <div className="text-slate-500 text-xs">Contact on site</div>
                  <div className="font-medium text-slate-900">{data.contact_name || '—'}</div>
                  <div className="text-xs text-slate-500">
                    {[data.contact_phone, data.contact_email].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
              </div>
            </div>

            {data.description && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                  Description
                </div>
                <p className="text-slate-800 whitespace-pre-line">{data.description}</p>
              </div>
            )}

            {/* ── Financial ─────────────────────────────────── */}
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">
                Financial
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-xs text-slate-500">Event cost</div>
                  <div className="font-bold text-slate-900">{fmtMoney(data.total_event_cost)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Requested</div>
                  <div className="font-bold text-slate-900">{fmtMoney(data.amount_requested)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Approved</div>
                  <div className="font-bold text-emerald-700">{fmtMoney(data.amount_approved)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Projected sales</div>
                  <div className="font-bold text-slate-900" data-testid="er-detail-projected-sales">
                    {Number(data.total_projected_sales || 0).toLocaleString()} sale{Number(data.total_projected_sales) === 1 ? '' : 's'}
                  </div>
                </div>
              </div>
              {/* Funding split — only rendered when the backend chose to
                  include these fields (i.e. viewer is KR or agency admin). */}
              {(data.funding_kr_amount !== undefined || data.funding_agency_amount !== undefined) && (
                <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-xs text-slate-500">KR contribution</div>
                    <div className="font-semibold text-slate-800">{fmtMoney(data.funding_kr_amount)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Agency contribution</div>
                    <div className="font-semibold text-slate-800">{fmtMoney(data.funding_agency_amount)}</div>
                  </div>
                  {data.internal_notes && (
                    <div className="md:col-span-3">
                      <div className="text-xs text-slate-500">Internal notes (not visible to agents)</div>
                      <div className="text-slate-800 whitespace-pre-line">{data.internal_notes}</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Projected sales breakdown ─────────────────── */}
            {(data.projected_sales || []).length > 0 && (
              <div className="rounded-lg border border-slate-200 p-4">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                  Projected sales by category
                </div>
                <div className="flex flex-wrap gap-2">
                  {data.projected_sales.map((l, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className="bg-slate-50 text-slate-700 border-slate-200 text-xs"
                    >
                      {l.category}: {Number(l.amount || 0).toLocaleString()} sale{Number(l.amount) === 1 ? '' : 's'}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* ── Participating agents ─────────────────────── */}
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
                  <Users size={13} /> Participating agents
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">
                    {(data.participating_agents || []).length} assigned
                  </span>
                  {canManage && (
                    <Button
                      variant="outline" size="sm" className="h-7 text-xs"
                      onClick={() => setShowParticipants(true)}
                      data-testid="er-manage-participants"
                    >
                      <Users size={11} className="mr-1" /> Manage
                    </Button>
                  )}
                </div>
              </div>
              {(data.participating_agents || []).length === 0 ? (
                <p className="text-xs text-slate-400 italic">No participants assigned yet.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {data.participating_agents.map((p, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className="bg-white border-slate-200 text-slate-700 text-xs"
                    >
                      {p.name}{p.npn ? ` · ${p.npn}` : ''}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* ── Calendar linkage ─────────────────────────── */}
            {data.linked_event_id && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm flex items-center justify-between">
                <div className="text-emerald-900">
                  This request is on the shared calendar.
                </div>
                <a
                  href={`/calendar?event_id=${data.linked_event_id}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-emerald-800 font-semibold text-xs inline-flex items-center gap-1"
                  data-testid="er-linked-event-link"
                >
                  Open calendar event <ExternalLink size={11} />
                </a>
              </div>
            )}

            {/* ── Timeline ─────────────────────────────────── */}
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                <ClipboardList size={13} /> Timeline
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {(data.timeline || []).slice().reverse().map((t, i) => (
                  <div key={i} className="text-xs text-slate-600 flex gap-2">
                    <span className="text-slate-400 font-mono whitespace-nowrap">
                      {fmtDate(t.ts)}
                    </span>
                    <span className="font-medium text-slate-800">{t.action}</span>
                    {t.actor_name && <span className="text-slate-500">by {t.actor_name}</span>}
                    {t.note && <span className="text-slate-500 italic">— {t.note}</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Attachments ──────────────────────────────── */}
            <AttachmentsSection
              data={data}
              requestId={requestId}
              onChanged={load}
            />

            {/* ── Live Capture kiosk (approved events only) ── */}
            {(data.status === 'approved' || data.status === 'partially_approved') && (
              <div className="rounded-lg border border-[#f5c14f]/40 bg-gradient-to-r from-[#f5c14f]/10 to-transparent p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#0f2547]">Live Event Capture</div>
                  <div className="text-xs text-slate-600">Collect leads on your tablet during the event — auto-linked to this event.</div>
                </div>
                <LiveCaptureButton
                  event={{
                    event_id: requestId,
                    name: data.event_name,
                    date: data.event_date,
                    kind: 'event',
                  }}
                  label="Open Kiosk"
                  size="md"
                />
              </div>
            )}

            {/* ── Leads Captured (ROI panel) ─────────────── */}
            {metrics && (
              <div
                className="rounded-lg border border-emerald-200 bg-emerald-50 p-4"
                data-testid="er-leads-captured-panel"
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="text-xs uppercase tracking-wider text-emerald-900 font-semibold flex items-center gap-1.5">
                    <TrendingUp size={13} /> Leads Captured
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-emerald-800 hover:bg-emerald-100"
                    onClick={() => {
                      onOpenChange?.(false);
                      navigate(`/leads?event_request_id=${requestId}`);
                    }}
                    disabled={!metrics.total}
                    data-testid="er-view-leads-btn"
                  >
                    View leads <ArrowRight size={12} className="ml-1" />
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-2xl font-bold text-emerald-900" data-testid="er-metric-total">
                      {metrics.total}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700">Total</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-emerald-900" data-testid="er-metric-closed">
                      {metrics.closed}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700">Closed Won</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-emerald-900" data-testid="er-metric-rate">
                      {(metrics.conversion_rate * 100).toFixed(1)}%
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700">Conv. Rate</div>
                  </div>
                </div>
                {metrics.total === 0 && (
                  <div className="mt-2 text-[11px] text-emerald-700 italic">
                    No leads captured yet — use the Live Kiosk to start.
                  </div>
                )}
              </div>
            )}

            {/* ── Payments (KR only, approved only) ───────── */}
            {isKR && (data.status === 'approved' || data.status === 'partially_approved' || data.status === 'completed') && (
              <PaymentsSection
                data={data}
                requestId={requestId}
                onChanged={load}
              />
            )}

            {/* ── Action panels ────────────────────────────── */}
            {action === 'approve' && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-3">
                <div className="text-sm font-semibold text-emerald-900">Approve request</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Amount approved *</Label>
                    <Input
                      type="number" min={0} step="1"
                      value={actionForm.amount_approved}
                      onChange={(e) => setActionForm((p) => ({ ...p, amount_approved: e.target.value }))}
                      data-testid="er-approve-amount"
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      Less than requested ({fmtMoney(data.amount_requested)}) → status becomes Partially Approved.
                    </p>
                  </div>
                  <div>
                    <Label>KR contribution</Label>
                    <Input
                      type="number" min={0} step="1"
                      value={actionForm.kr_amount}
                      onChange={(e) => setActionForm((p) => ({ ...p, kr_amount: e.target.value }))}
                      data-testid="er-approve-kr"
                    />
                  </div>
                  <div>
                    <Label>Agency contribution</Label>
                    <Input
                      type="number" min={0} step="1"
                      value={actionForm.agency_amount}
                      onChange={(e) => setActionForm((p) => ({ ...p, agency_amount: e.target.value }))}
                      data-testid="er-approve-agency"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Internal notes (not visible to agents)</Label>
                    <Textarea
                      rows={2}
                      value={actionForm.internal_notes}
                      onChange={(e) => setActionForm((p) => ({ ...p, internal_notes: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setAction(null)} disabled={saving}>Cancel</Button>
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={confirmApprove} disabled={saving}
                    data-testid="er-approve-confirm"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                    Confirm approval
                  </Button>
                </div>
              </div>
            )}

            {action === 'decline' && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 space-y-3">
                <div className="text-sm font-semibold text-rose-900">Decline request</div>
                <Textarea
                  rows={3}
                  value={actionForm.reason}
                  onChange={(e) => setActionForm((p) => ({ ...p, reason: e.target.value }))}
                  placeholder="Reason (visible to requester)…"
                  data-testid="er-decline-reason"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setAction(null)} disabled={saving}>Cancel</Button>
                  <Button
                    size="sm" variant="destructive"
                    onClick={confirmDecline}
                    disabled={saving || !actionForm.reason.trim()}
                    data-testid="er-decline-confirm"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                    Decline
                  </Button>
                </div>
              </div>
            )}

            {action === 'needs_info' && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
                <div className="text-sm font-semibold text-amber-900">Request more info</div>
                <Textarea
                  rows={3}
                  value={actionForm.note}
                  onChange={(e) => setActionForm((p) => ({ ...p, note: e.target.value }))}
                  placeholder="What additional info do you need?"
                  data-testid="er-needsinfo-note"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setAction(null)} disabled={saving}>Cancel</Button>
                  <Button
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={confirmNeedsInfo}
                    disabled={saving || !actionForm.note.trim()}
                    data-testid="er-needsinfo-confirm"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                    Send back to requester
                  </Button>
                </div>
              </div>
            )}

            {action === 'adjust' && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-3" data-testid="er-adjust-panel">
                <div className="text-sm font-semibold text-emerald-900">Adjust approval</div>
                <p className="text-[11px] text-emerald-900/70">
                  Update the amounts and funding split for an already-approved request. Leave a field blank to keep its current value.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Amount approved</Label>
                    <Input
                      type="number" min={0} step="1"
                      value={actionForm.amount_approved}
                      onChange={(e) => setActionForm((p) => ({ ...p, amount_approved: e.target.value }))}
                      placeholder={String(data?.amount_approved ?? '')}
                      data-testid="er-adjust-amount"
                    />
                  </div>
                  <div>
                    <Label>KR contribution</Label>
                    <Input
                      type="number" min={0} step="1"
                      value={actionForm.kr_amount}
                      onChange={(e) => setActionForm((p) => ({ ...p, kr_amount: e.target.value }))}
                      placeholder={String(data?.funding_kr_amount ?? '')}
                      data-testid="er-adjust-kr"
                    />
                  </div>
                  <div>
                    <Label>Agency contribution</Label>
                    <Input
                      type="number" min={0} step="1"
                      value={actionForm.agency_amount}
                      onChange={(e) => setActionForm((p) => ({ ...p, agency_amount: e.target.value }))}
                      placeholder={String(data?.funding_agency_amount ?? '')}
                      data-testid="er-adjust-agency"
                    />
                  </div>
                  <div>
                    <Label>Reason (audit trail)</Label>
                    <Input
                      value={actionForm.reason}
                      onChange={(e) => setActionForm((p) => ({ ...p, reason: e.target.value }))}
                      placeholder="Why the adjustment?"
                      data-testid="er-adjust-reason"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Internal notes</Label>
                    <Textarea
                      rows={2}
                      value={actionForm.internal_notes}
                      onChange={(e) => setActionForm((p) => ({ ...p, internal_notes: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setAction(null)} disabled={saving}>Cancel</Button>
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={confirmAdjust} disabled={saving}
                    data-testid="er-adjust-confirm"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                    Save adjustment
                  </Button>
                </div>
              </div>
            )}

            {action === 'cancel' && (
              <div className="rounded-lg border border-slate-300 bg-slate-50 p-4 space-y-3" data-testid="er-cancel-panel">
                <div className="text-sm font-semibold text-slate-900">Cancel this event request</div>
                <p className="text-[11px] text-slate-600">
                  The request is kept in the record but marked as cancelled. Any linked calendar event will be removed.
                </p>
                <Textarea
                  rows={3}
                  value={actionForm.reason}
                  onChange={(e) => setActionForm((p) => ({ ...p, reason: e.target.value }))}
                  placeholder="Reason (optional, visible to requester)…"
                  data-testid="er-cancel-reason"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setAction(null)} disabled={saving}>Back</Button>
                  <Button
                    size="sm" variant="destructive"
                    onClick={confirmCancel}
                    disabled={saving}
                    data-testid="er-cancel-confirm"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                    Cancel event request
                  </Button>
                </div>
              </div>
            )}

            {action === 'reopen' && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3" data-testid="er-reopen-panel">
                <div className="text-sm font-semibold text-blue-900">Reopen request</div>
                <p className="text-[11px] text-blue-900/80">
                  This moves the request back to <strong>Submitted</strong> so it can go through review again.
                </p>
                <Textarea
                  rows={2}
                  value={actionForm.note}
                  onChange={(e) => setActionForm((p) => ({ ...p, note: e.target.value }))}
                  placeholder="Note (optional)…"
                  data-testid="er-reopen-note"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setAction(null)} disabled={saving}>Back</Button>
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={confirmReopen} disabled={saving}
                    data-testid="er-reopen-confirm"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                    Reopen
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && data && !action && (
          <DialogFooter className="flex-wrap gap-2">
            {canManage && (
              <Button
                variant="outline" size="sm"
                onClick={() => setShowEdit(true)}
                data-testid="er-edit-btn"
              >
                <Pencil size={14} className="mr-1" /> Edit
              </Button>
            )}
            {canReopen && (
              <Button
                variant="outline" size="sm"
                className="text-blue-700 border-blue-200 hover:bg-blue-50"
                onClick={() => setAction('reopen')}
                data-testid="er-open-reopen"
              >
                <RotateCcw size={14} className="mr-1" /> Reopen
              </Button>
            )}
            {canAdjust && (
              <Button
                variant="outline" size="sm"
                className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                onClick={() => {
                  setActionForm((p) => ({
                    ...p,
                    amount_approved: '',
                    kr_amount: '',
                    agency_amount: '',
                    internal_notes: '',
                    reason: '',
                  }));
                  setAction('adjust');
                }}
                data-testid="er-open-adjust"
              >
                <Wallet size={14} className="mr-1" /> Adjust approval
              </Button>
            )}
            {showSendToAgency && (
              <Button
                variant="outline" size="sm"
                onClick={submitAgencyReview} disabled={saving}
                data-testid="er-send-to-agency"
              >
                Send to Agency Review
              </Button>
            )}
            {showSendToKr && (
              <Button
                variant="outline" size="sm"
                onClick={submitKrReview} disabled={saving}
                data-testid="er-send-to-kr"
              >
                Send to KR Review
              </Button>
            )}
            {showNeedsInfo && (
              <Button
                variant="outline" size="sm"
                onClick={() => setAction('needs_info')}
                data-testid="er-open-needsinfo"
              >
                <HelpCircle size={14} className="mr-1" /> Needs Info
              </Button>
            )}
            {showDecline && (
              <Button
                variant="outline" size="sm"
                className="text-rose-700 border-rose-200 hover:bg-rose-50"
                onClick={() => setAction('decline')}
                data-testid="er-open-decline"
              >
                <XCircle size={14} className="mr-1" /> Decline
              </Button>
            )}
            {canCancel && (
              <Button
                variant="outline" size="sm"
                className="text-slate-700 border-slate-300 hover:bg-slate-100"
                onClick={() => {
                  setActionForm((p) => ({ ...p, reason: '' }));
                  setAction('cancel');
                }}
                data-testid="er-open-cancel"
              >
                <Ban size={14} className="mr-1" /> Cancel
              </Button>
            )}
            {showApprove && (
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => setAction('approve')}
                data-testid="er-open-approve"
              >
                <CheckCircle2 size={14} className="mr-1" /> Approve
              </Button>
            )}
            {isKR && (status === 'approved' || status === 'partially_approved') && (
              <Button
                size="sm" variant="outline"
                onClick={() => runAction(
                  () => eventRequestsAPI.complete(requestId),
                  'Event marked completed',
                )}
                disabled={saving}
                data-testid="er-complete"
              >
                Mark completed
              </Button>
            )}
            {canDelete && (
              <Button
                variant="outline" size="sm"
                className="text-rose-700 border-rose-300 hover:bg-rose-50 ml-auto"
                onClick={() => setConfirmDelete(true)}
                data-testid="er-delete-btn"
              >
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>

      {/* Phase 27.48 — Edit form modal */}
      <EventRequestFormDialog
        open={showEdit}
        editing={showEdit ? data : null}
        onOpenChange={setShowEdit}
        onSaved={() => {
          setShowEdit(false);
          load();
          onChanged?.();
        }}
      />

      {/* Phase 27.48 — Participants manager */}
      <ManageParticipantsDialog
        open={showParticipants}
        onOpenChange={setShowParticipants}
        requestId={requestId}
        current={data?.participating_agents || []}
        onSaved={() => { load(); onChanged?.(); }}
      />

      {/* Phase 27.50.B.11 — Delete-with-reason dialog */}
      <DeleteRequestDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        resourceLabel="event request"
        summary={data ? `${data.event_name || 'Event'} — ${data.city || ''}, ${data.state || ''}` : ''}
        onConfirm={confirmHardDelete}
      />
    </Dialog>
  );
}


const ATTACHMENT_KINDS = [
  { value: 'flyer', label: 'Flyer / marketing' },
  { value: 'invoice', label: 'Invoice / receipt' },
  { value: 'sponsorship', label: 'Sponsorship' },
  { value: 'contract', label: 'Contract' },
  { value: 'other', label: 'Other' },
];

function AttachmentsSection({ data, requestId, onChanged }) {
  const [showUpload, setShowUpload] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState('other');
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const attachments = data?.attachments || [];

  const submit = async () => {
    if (!file) { toast.error('Pick a file first'); return; }
    if (!title.trim()) { toast.error('Give this attachment a title'); return; }
    setUploading(true);
    try {
      await eventRequestsAPI.uploadAttachment(requestId, file, title.trim(), kind);
      toast.success('Attachment uploaded');
      setShowUpload(false);
      setFile(null);
      setTitle('');
      setKind('other');
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const download = async (idx, att) => {
    setDownloading(idx);
    try {
      const r = await eventRequestsAPI.downloadAttachment(requestId, idx);
      const blob = new Blob([r.data], { type: att.content_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename || att.title || 'attachment';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-4" data-testid="er-attachments">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
          <Paperclip size={13} /> Attachments ({attachments.length})
        </div>
        <Button
          variant="outline" size="sm"
          onClick={() => setShowUpload((v) => !v)}
          data-testid="er-attachment-upload-toggle"
        >
          <Upload size={12} className="mr-1" /> {showUpload ? 'Cancel' : 'Upload file'}
        </Button>
      </div>

      {showUpload && (
        <div className="mb-3 p-3 rounded border border-slate-200 bg-slate-50 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Title *</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Venue invoice"
                data-testid="er-attachment-title"
              />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="er-attachment-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ATTACHMENT_KINDS.map((k) => (
                    <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">File</Label>
            <Input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="er-attachment-file"
            />
            <p className="text-[10px] text-slate-500 mt-1">Max 15 MB.</p>
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={submit}
              disabled={uploading}
              data-testid="er-attachment-submit"
            >
              {uploading && <Loader2 size={12} className="mr-1 animate-spin" />}
              Upload
            </Button>
          </div>
        </div>
      )}

      {attachments.length === 0 ? (
        <p className="text-xs text-slate-400 italic">No files yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {attachments.map((a, i) => (
            <li key={i} className="py-2 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{a.title}</p>
                <p className="text-[11px] text-slate-500 truncate">
                  {a.filename} · <span className="capitalize">{a.kind}</span>
                  {a.size_bytes ? ` · ${Math.round(a.size_bytes / 1024)} KB` : ''}
                </p>
              </div>
              <Button
                size="sm" variant="ghost"
                onClick={() => download(i, a)}
                disabled={downloading === i}
                data-testid={`er-attachment-download-${i}`}
              >
                {downloading === i
                  ? <Loader2 size={12} className="animate-spin" />
                  : <Download size={12} />}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const PAYMENT_METHODS = [
  { value: 'check', label: 'Check' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'cashapp', label: 'Cash App' },
  { value: 'eft', label: 'EFT / ACH' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_RECIPIENT_TYPES = [
  { value: 'agent', label: 'Agent' },
  { value: 'agency', label: 'Agency' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_STATUSES = [
  { value: 'pending', label: 'Pending', tone: 'bg-amber-100 text-amber-800 border-amber-200' },
  { value: 'scheduled', label: 'Scheduled', tone: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 'paid', label: 'Paid', tone: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  { value: 'failed', label: 'Failed', tone: 'bg-rose-100 text-rose-800 border-rose-200' },
];

function PaymentsSection({ data, requestId, onChanged }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    recipient_type: 'agency', recipient_name: '', method: 'check',
    amount: '', status: 'pending', payment_date: '', reference: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const payments = data?.payments || [];
  const paid = payments
    .filter((p) => p.status === 'paid')
    .reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const approved = Number(data?.amount_approved || 0);
  const remaining = Math.max(approved - paid, 0);

  const submit = async () => {
    if (!form.recipient_name.trim()) { toast.error('Recipient name is required'); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Amount must be > 0'); return; }
    setSaving(true);
    try {
      await eventRequestsAPI.addPayment(requestId, {
        recipient_type: form.recipient_type,
        recipient_name: form.recipient_name.trim(),
        method: form.method,
        amount: Number(form.amount),
        status: form.status,
        payment_date: form.payment_date || null,
        reference: form.reference || null,
        notes: form.notes || null,
      });
      toast.success('Payment recorded');
      setShowForm(false);
      setForm({
        recipient_type: 'agency', recipient_name: '', method: 'check',
        amount: '', status: 'pending', payment_date: '', reference: '', notes: '',
      });
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-4" data-testid="er-payments">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
          <CreditCard size={13} /> Payment tracking
        </div>
        <Button
          variant="outline" size="sm"
          onClick={() => setShowForm((v) => !v)}
          data-testid="er-payment-toggle"
        >
          <Plus size={12} className="mr-1" /> {showForm ? 'Cancel' : 'Add payment'}
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
        <div className="rounded border border-slate-200 py-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Approved</div>
          <div className="text-sm font-bold text-slate-900">{fmtMoney(approved)}</div>
        </div>
        <div className="rounded border border-emerald-200 bg-emerald-50 py-2">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Paid</div>
          <div className="text-sm font-bold text-emerald-800">{fmtMoney(paid)}</div>
        </div>
        <div className="rounded border border-amber-200 bg-amber-50 py-2">
          <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Remaining</div>
          <div className="text-sm font-bold text-amber-800">{fmtMoney(remaining)}</div>
        </div>
      </div>

      {showForm && (
        <div className="mb-3 p-3 rounded border border-slate-200 bg-slate-50 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Recipient type</Label>
              <Select
                value={form.recipient_type}
                onValueChange={(v) => setForm((p) => ({ ...p, recipient_type: v }))}
              >
                <SelectTrigger data-testid="er-payment-recipient-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_RECIPIENT_TYPES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Recipient name *</Label>
              <Input
                value={form.recipient_name}
                onChange={(e) => setForm((p) => ({ ...p, recipient_name: e.target.value }))}
                data-testid="er-payment-recipient-name"
              />
            </div>
            <div>
              <Label className="text-xs">Method</Label>
              <Select
                value={form.method}
                onValueChange={(v) => setForm((p) => ({ ...p, method: v }))}
              >
                <SelectTrigger data-testid="er-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Amount *</Label>
              <Input
                type="number" min={0} step="1"
                value={form.amount}
                onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                data-testid="er-payment-amount"
              />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}
              >
                <SelectTrigger data-testid="er-payment-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Payment date</Label>
              <Input
                type="date"
                value={form.payment_date}
                onChange={(e) => setForm((p) => ({ ...p, payment_date: e.target.value }))}
                data-testid="er-payment-date"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Reference (check #, txn id, etc.)</Label>
              <Input
                value={form.reference}
                onChange={(e) => setForm((p) => ({ ...p, reference: e.target.value }))}
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Notes</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={submit}
              disabled={saving}
              data-testid="er-payment-submit"
            >
              {saving && <Loader2 size={12} className="mr-1 animate-spin" />}
              Save payment
            </Button>
          </div>
        </div>
      )}

      {payments.length === 0 ? (
        <p className="text-xs text-slate-400 italic">No payments recorded yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left py-1.5 pr-2 font-semibold">Recipient</th>
                <th className="text-left py-1.5 pr-2 font-semibold">Method</th>
                <th className="text-right py-1.5 pr-2 font-semibold">Amount</th>
                <th className="text-left py-1.5 pr-2 font-semibold">Status</th>
                <th className="text-left py-1.5 pr-2 font-semibold">Date</th>
                <th className="text-left py-1.5 font-semibold">Ref.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payments.map((p) => {
                const st = PAYMENT_STATUSES.find((s) => s.value === p.status);
                return (
                  <tr key={p.payment_id} data-testid={`er-payment-row-${p.payment_id}`}>
                    <td className="py-1.5 pr-2">
                      <div className="text-slate-900">{p.recipient_name}</div>
                      <div className="text-[10px] text-slate-500 capitalize">{p.recipient_type}</div>
                    </td>
                    <td className="py-1.5 pr-2 capitalize">{p.method}</td>
                    <td className="py-1.5 pr-2 text-right font-semibold">{fmtMoney(p.amount)}</td>
                    <td className="py-1.5 pr-2">
                      <Badge variant="outline" className={`${st?.tone || 'bg-slate-100'} text-[10px]`}>
                        {st?.label || p.status}
                      </Badge>
                    </td>
                    <td className="py-1.5 pr-2 whitespace-nowrap">{p.payment_date ? fmtDate(p.payment_date) : '—'}</td>
                    <td className="py-1.5 truncate max-w-[140px]" title={p.reference}>
                      {p.reference || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
